/**
 * Utility functions for handling UTC timestamps from the backend
 */

import { format as dateFnsFormat } from 'date-fns';

/**
 * Safely format a date string, with fallback for invalid dates
 * @param dateString - Date string to format
 * @param formatString - Date format string (e.g., 'MMM dd, yyyy HH:mm')
 * @param fallback - Fallback string if date is invalid
 * @returns Formatted date string or fallback
 */
export const safeFormatDate = (
  dateString: string | null | undefined, 
  formatString: string, 
  fallback: string = 'Invalid date'
): string => {
  if (!dateString) return fallback;
  
  try {
    const date = parseUTCDate(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return fallback;
    }
    return dateFnsFormat(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return fallback;
  }
};

/**
 * Parse a UTC timestamp string correctly, ensuring timezone independence
 * @param dateString - ISO 8601 date string from backend
 * @returns Date object in UTC
 */
export const parseUTCDate = (dateString: string | null | undefined): Date => {
  // Handle null or undefined values
  if (!dateString) {
    return new Date(); // Return current date if no date string provided
  }
  
  // Ensure the date string is treated as UTC
  const date = new Date(dateString);
  // If the date string doesn't include timezone info, treat it as UTC
  if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    return new Date(dateString + 'Z');
  }
  return date;
};

/**
 * Calculate duration between two UTC timestamps
 * @param startDate - Start timestamp string
 * @param endDate - End timestamp string (optional, defaults to current time)
 * @returns Duration in milliseconds
 */
export const calculateDuration = (startDate: string, endDate?: string): number => {
  const start = parseUTCDate(startDate);
  const end = endDate ? parseUTCDate(endDate) : new Date();
  return end.getTime() - start.getTime();
};

/**
 * Format duration in a human-readable format
 * @param durationMs - Duration in milliseconds
 * @returns Formatted duration string
 */
export const formatDuration = (durationMs: number): string => {
  const diffSecs = Math.floor(durationMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  if (diffYears > 0) {
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    return remainingMonths > 0 ? `${diffYears}y ${remainingMonths}mo` : `${diffYears}y`;
  } else if (diffMonths > 0) {
    const remainingDays = diffDays % 30;
    return remainingDays > 0 ? `${diffMonths}mo ${remainingDays}d` : `${diffMonths}mo`;
  } else if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return remainingHours > 0 ? `${diffDays}d ${remainingHours}h` : `${diffDays}d`;
  } else if (diffHours > 0) {
    const remainingMins = diffMins % 60;
    return remainingMins > 0 ? `${diffHours}h ${remainingMins}m` : `${diffHours}h`;
  } else if (diffMins > 0) {
    const remainingSecs = diffSecs % 60;
    return remainingSecs > 0 ? `${diffMins}m ${remainingSecs}s` : `${diffMins}m`;
  }
  return `${diffSecs}s`;
};

/**
 * Format a date range string from "YYYY-MM-DD to YYYY-MM-DD" to "DD-MM-YYYY to DD-MM-YYYY"
 */
export const formatDateRange = (dateRange: string): string => {
  return dateRange.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1');
};